/**
 * Perfora GA4 Ecommerce dataLayer
 * Portable module — pair with snippets/ga4-datalayer.liquid + snippets/ga4-item.liquid
 *
 * Markup hooks:
 *   [data-ga4-list][data-ga4-list-id][data-ga4-list-name]
 *   [data-ga4-item]  — JSON item payload on product card
 *   [data-ga4-promo][data-ga4-promo-id][data-ga4-promo-name]
 *     [data-ga4-creative-slot][data-ga4-creative-name]
 */
(function () {
  'use strict';

  var CFG = window.PerforaGA4 || {};
  var STORAGE_KEY = 'perfora_ga4_list_attr';
  var META_KEY = 'perfora_ga4_item_meta';
  var viewedPromos = new WeakSet();
  var viewedItems = new Set();
  var selectBound = new WeakSet();
  // GA4 FIX (F-19): observeAll() lives inside observeViewport(); PerforaGA4.reobserve
  // referenced it from module scope and threw ReferenceError on every call.
  var observeAllRef = null;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function platform() {
    return window.matchMedia('(max-width: 768px)').matches ? 'm-web' : 'd-web';
  }

  function commonParams() {
    var pType = CFG.page_type || 'other_page';
    var path = (window.location.pathname || '').toLowerCase();
    var query = (window.location.search || '').toLowerCase();
    if (path.indexOf('/search') !== -1 || path.indexOf('searchtap') !== -1 || (path.indexOf('/pages/') !== -1 && path.indexOf('search') !== -1) || query.indexOf('q=') !== -1) {
      pType = 'search_page';
    }
    var params = {
      page_type: pType,
      platform: platform(),
      user_status: CFG.user_status || 'guest'
    };
    if (CFG.user_status === 'logged_in' && CFG.user_id_event != null) {
      params.user_id_event = String(CFG.user_id_event);
    } else {
      params.user_id_event = undefined;
    }
    return params;
  }

  function pushEvent(eventName, ecommerce, extra) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    var payload = Object.assign({ event: eventName }, commonParams(), extra || {}, {
      ecommerce: ecommerce
    });
    window.dataLayer.push(payload);
    if (window.PerforaGA4 && window.PerforaGA4.debug) {
      console.log('[GA4]', eventName, payload);
    }
  }

  // GA4 FIX (F-08): "NA" is truthy, so `a || b` used to let a placeholder beat
  // a real value. Every list-attribution merge now goes through this.
  function real(v) {
    if (v == null || v === '' || v === 'NA') return null;
    if (typeof v === 'object') {
      if (Array.isArray(v)) return v.length ? v.join(', ') : null;
      if (v.value != null) return real(v.value);
      return null;
    }
    return String(v).trim();
  }

  // GA4 FIX (F-07): free products auto-added with a paid product must not
  // generate events of their own.
  function isFreeLine(line) {
    if (!line) return false;
    var props = line.properties || {};
    if (props.freeGift || props._free_product || props['_free_product']) return true;
    var price = line.final_price != null ? line.final_price : line.price;
    if (price === 0) return true;
    var fg = window.freeGiftSettings || {};
    var vid = String(line.variant_id || line.id || '');
    for (var k in fg) {
      if (fg[k] && String(fg[k].variantId) === vid) return true;
    }
    return false;
  }

  function money(n) {
    var v = Number(n);
    return isNaN(v) ? 0 : Math.round(v * 100) / 100;
  }

  function parseItem(el) {
    if (!el) return null;
    var raw = el.getAttribute('data-ga4-item');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function getListContext(el) {
    var list = el.closest('[data-ga4-list]');
    if (!list) {
      return {
        item_list_id: 'NA',
        item_list_name: 'NA',
        filter_applied: CFG.filter_applied || 'NA',
        sort_applied: CFG.sort_applied || 'NA'
      };
    }
    return {
      item_list_id: list.getAttribute('data-ga4-list-id') || 'NA',
      item_list_name: list.getAttribute('data-ga4-list-name') || 'NA',
      filter_applied: list.getAttribute('data-ga4-filter') || CFG.filter_applied || 'NA',
      sort_applied: list.getAttribute('data-ga4-sort') || CFG.sort_applied || 'NA'
    };
  }

  function indexInList(el, list) {
    if (!list) return 0;
    var items = Array.prototype.slice.call(list.querySelectorAll('[data-ga4-item]'));
    var idx = items.indexOf(el);
    return idx < 0 ? 0 : idx;
  }

  function enrichItem(item, el) {
    if (!item) return null;
    var ctx = getListContext(el);
    var list = el.closest('[data-ga4-list]');
    var stored = readListAttr(item.item_id);
    return Object.assign({}, item, {
      item_list_id: real(item.item_list_id) || real(stored.item_list_id) || real(ctx.item_list_id) || 'NA',
      item_list_name: real(item.item_list_name) || real(stored.item_list_name) || real(ctx.item_list_name) || 'NA',
      index: typeof item.index === 'number' ? item.index : indexInList(el, list),
      filter_applied: ctx.filter_applied,
      sort_applied: ctx.sort_applied,
      coupon: item.coupon || 'NA',
      discount: money(item.discount || 0),
      price: money(item.price || 0),
      quantity: item.quantity || 1,
      affiliation: item.affiliation || CFG.shop_name || 'NA'
    });
  }

  function stripInternal(item, includeFilters) {
    var copy = Object.assign({}, item);
    delete copy.product_id;
    delete copy.variant_id;
    if (!includeFilters) {
      delete copy.filter_applied;
      delete copy.sort_applied;
    }
    return copy;
  }

  function saveListAttr(item) {
    try {
      if (!real(item.item_list_id)) return; // GA4 FIX (F-08): never store a placeholder
      var map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      var entry = {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        index: item.index
      };
      [item.item_id, item.variant_id, item.product_id, item.handle].forEach(function (k) {
        if (k != null && k !== '') map[String(k)] = entry;
      });
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  function readListAttr() {
    try {
      var map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      for (var i = 0; i < arguments.length; i++) {
        var k = arguments[i];
        if (k != null && k !== '' && map[String(k)]) return map[String(k)];
      }
      return {};
    } catch (e) {
      return {};
    }
  }

  // GA4 FIX (Audit-25-08): item_category / 2 / 3 arrived as "NA" on add_to_cart,
  // view_cart and begin_checkout. Those payloads are rebuilt by itemFromCartLine()
  // from /cart.js, which carries no metafields and no tags -- so unless the
  // product's card happened to still be in the DOM, every category collapsed to
  // "NA". Liquid renders the real values once (product cards, CFG.cart.items,
  // CFG.product); we cache them per variant/item id and read them back here.
  var META_FIELDS = [
    'item_name', 'item_brand', 'affiliation', 'item_variant',
    'item_category', 'item_category2', 'item_category3',
    'item_category4', 'item_category5'
  ];

  function metaMap() {
    try { return JSON.parse(sessionStorage.getItem(META_KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function saveItemMeta(item) {
    if (!item) return;
    try {
      var map = metaMap();
      var slim = {};
      META_FIELDS.forEach(function (f) {
        if (real(item[f])) slim[f] = item[f];
      });
      if (!Object.keys(slim).length) return;
      [item.variant_id, item.item_id, item.product_id].forEach(function (k) {
        if (k == null || k === '') return;
        map['k' + String(k)] = Object.assign({}, map['k' + String(k)], slim);
      });
      sessionStorage.setItem(META_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  function readItemMeta() {
    var map = metaMap();
    for (var i = 0; i < arguments.length; i++) {
      var k = arguments[i];
      if (k == null || k === '') continue;
      var hit = map['k' + String(k)];
      if (hit) return hit;
    }
    return {};
  }

  // Seed the cache from everything Liquid rendered on this page.
  function seedItemMeta() {
    document.querySelectorAll('[data-ga4-item]').forEach(function (el) {
      saveItemMeta(parseItem(el));
    });
    if (CFG.cart && CFG.cart.items) CFG.cart.items.forEach(saveItemMeta);
    if (CFG.product) saveItemMeta(CFG.product);
  }

  function itemsValue(items) {
    return money(
      (items || []).reduce(function (sum, it) {
        return sum + money(it.price) * (it.quantity || 1);
      }, 0)
    );
  }

  function promoPayload(el) {
    return {
      promotion_id: el.getAttribute('data-ga4-promo-id') || 'NA',
      promotion_name: el.getAttribute('data-ga4-promo-name') || 'NA',
      creative_slot: el.getAttribute('data-ga4-creative-slot') || 'NA',
      creative_name: el.getAttribute('data-ga4-creative-name') || 'NA'
    };
  }

  function trackViewPromotion(el) {
    if (!el || viewedPromos.has(el)) return;
    viewedPromos.add(el);
    pushEvent('view_promotion_ce', promoPayload(el));
  }

  function trackSelectPromotion(el) {
    if (!el) return;
    pushEvent('select_promotion_ce', promoPayload(el));
  }

  // GA4 FIX (F-11): select_promotion used to fire on ANY click inside the
  // banner, including non-clickable decorative ones. It now requires the
  // click to land on an actual navigating element -- a real <a href> (not
  // "#" or empty) or an element explicitly marked as the promo's CTA via
  // data-ga4-promo-cta.
  function isPromoCta(target, promoEl) {
    var link = target.closest('a[href]');
    if (link && promoEl.contains(link)) {
      var href = link.getAttribute('href') || '';
      if (href !== '' && href !== '#') return true;
    }
    var cta = target.closest('[data-ga4-promo-cta]');
    return !!(cta && promoEl.contains(cta));
  }

  function bindPromoClicks(root) {
    (root || document).querySelectorAll('[data-ga4-promo]').forEach(function (el) {
      if (selectBound.has(el)) return;
      selectBound.add(el);
      el.addEventListener('click', function (e) {
        if (!isPromoCta(e.target, el)) return;
        trackSelectPromotion(el);
      }, true);
    });
  }

  function trackViewItemList(listEl, visibleEls) {
    if (!listEl || !visibleEls.length) return;
    var listId = listEl.getAttribute('data-ga4-list-id') || 'NA';
    var listName = listEl.getAttribute('data-ga4-list-name') || 'NA';
    var filter = listEl.getAttribute('data-ga4-filter') || CFG.filter_applied || 'NA';
    var sort = listEl.getAttribute('data-ga4-sort') || CFG.sort_applied || 'NA';
    var keyPrefix = listId + '::';
    var items = [];

    visibleEls.forEach(function (el) {
      var itemKey = keyPrefix + (el.getAttribute('data-ga4-item-id') || el.getAttribute('data-ga4-item') || '');
      if (viewedItems.has(itemKey)) return;
      var item = enrichItem(parseItem(el), el);
      if (!item) return;
      viewedItems.add(itemKey);
      saveItemMeta(item);
      // GA4 FIX (F-03): the item's own list wins when it has one (Shop by
      // Concern carries a per-concern list inside one shared container).
      item.item_list_id = real(item.item_list_id) || real(listId) || 'NA';
      item.item_list_name = real(item.item_list_name) || real(listName) || 'NA';
      item.filter_applied = filter;
      item.sort_applied = sort;
      saveListAttr(item);
      items.push(stripInternal(item, true));
    });

    if (!items.length) return;
    pushEvent('view_item_list_ce', {
      currency: CFG.currency || 'INR',
      item_list_id: listId,
      item_list_name: listName,
      items: items
    });
  }

  function trackSelectItem(el) {
    var item = enrichItem(parseItem(el), el);
    if (!item) return;
    saveListAttr(item);
    pushEvent('select_item_ce', {
      currency: CFG.currency || 'INR',
      item_list_id: item.item_list_id,
      item_list_name: item.item_list_name,
      items: [stripInternal(item, false)]
    });
  }

  function bindItemClicks(root) {
    (root || document).querySelectorAll('[data-ga4-item]').forEach(function (el) {
      if (selectBound.has(el)) return;
      selectBound.add(el);
      el.addEventListener('click', function (e) {
        var link = e.target.closest('a[href]');
        var quickBuy = e.target.closest('[onclick*="dialogQuickShop"], .quickbuy__btn, .product__quickbuy button');
        if (!link || quickBuy) return;
        trackSelectItem(el);
      }, true);
    });
  }

  function trackViewItem() {
    if (CFG.page_type !== 'product_page' || !CFG.product) return;
    var item = Object.assign({}, CFG.product);
    var pathParts = window.location.pathname.split('/products/');
    var handle = pathParts[1] ? pathParts[1].split('/')[0].split('?')[0] : '';
    var stored = readListAttr(item.variant_id, item.item_id, item.product_id, handle);
    if (stored.item_list_id) {
      item.item_list_id = stored.item_list_id;
      item.item_list_name = stored.item_list_name;
      item.index = stored.index != null ? stored.index : item.index;
    }
    pushEvent('view_item_ce', {
      currency: CFG.currency || 'INR',
      value: money(item.price * (item.quantity || 1)),
      items: [stripInternal(item, false)]
    });
  }

  function cartItemsFromConfig() {
    return (CFG.cart && CFG.cart.items ? CFG.cart.items : [])
      .filter(function (it) { return !isFreeLine(it); })
      .map(function (it) {
      var stored = readListAttr(it.variant_id, it.item_id, it.product_id, it.sku);
      return stripInternal(
        Object.assign({}, it, {
          item_list_id: real(stored.item_list_id) || real(it.item_list_id) || 'NA',
          item_list_name: real(stored.item_list_name) || real(it.item_list_name) || 'NA',
          index: stored.index != null ? stored.index : it.index
        }),
        false
      );
    });
  }

  // GA4 FIX (F-06): Perfora checks out from a drawer, so gating this on
  // page_type === 'cart_page' meant view_cart could never fire. It now fires on
  // the cart page AND every time the drawer opens (deduped per open).
  var viewCartPending = false;
  function trackViewCart() {
    // Add-to-cart opens the drawer optimistically (before the add finishes) with
    // window.__pfAdding set; skip that one -- the real open right after the add
    // response fires view_cart with the updated cart.
    if (window.__pfAdding) return;
    if (viewCartPending) return;
    viewCartPending = true;
    setTimeout(function () { viewCartPending = false; }, 400);
    var items = cartItemsFromConfig();
    if (!items.length) return;
    pushEvent('view_cart_ce', {
      currency: CFG.currency || 'INR',
      value: itemsValue(items),
      items: items
    });
  }

  // Watch for the cart drawer becoming visible or cart icon clicks.
  function bindCartDrawerOpen() {
    var SELECTORS = [
      'ecx-cart-drawer', '#ecxCartDrawer', '.cart-drawer', '#cart-drawer',
      '[data-cart-drawer]', '.js-cart-drawer',
      '.newfxcart', '#newfxcart',
      '.cart-deal-rail', '#cart-deal-rail',
      '.cart-notification-panel'
    ];
    var drawers = document.querySelectorAll(SELECTORS.join(','));

    function isOpen(el) {
      if (!el) return false;
      if (el.hasAttribute('open')) return true;
      var cls = el.className || '';
      if (typeof cls !== 'string') cls = el.getAttribute('class') || '';
      if (/(^|\s)(is-open|active|open|cart-drawer--active)(\s|$)/.test(cls)) return true;
      return el.getAttribute('aria-hidden') === 'false';
    }

    drawers.forEach(function (drawer) {
      var wasOpen = isOpen(drawer);
      new MutationObserver(function () {
        var nowOpen = isOpen(drawer);
        if (nowOpen && !wasOpen) {
          refreshCartThen(trackViewCart);
        }
        wasOpen = nowOpen;
      }).observe(drawer, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
    });

    // Directly bind click on cart buttons/icons across the site
    document.querySelectorAll('.js-header-cart-status-btn, .header__icon--cart a, a[href*="/cart"], [aria-controls="ecxCartDrawer"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setTimeout(function () {
          refreshCartThen(trackViewCart);
        }, 150);
      });
    });

    // Hook directly into window.ecxCartDrawer.openDrawer if available
    if (window.ecxCartDrawer && typeof window.ecxCartDrawer.openDrawer === 'function') {
      var origOpen = window.ecxCartDrawer.openDrawer;
      window.ecxCartDrawer.openDrawer = function () {
        var res = origOpen.apply(this, arguments);
        refreshCartThen(trackViewCart);
        return res;
      };
    }
  }

  // GA4 FIX (F-16): coupon was hardcoded 'NA' even when a discount was
  // applied in the drawer. Cart-level discounts appear on the live cart
  // object as cart_level_discount_applications.
  function currentCoupon() {
    var raw = CFG.cart && CFG.cart._raw;
    var apps = raw && raw.cart_level_discount_applications;
    if (apps && apps.length && apps[0].title) return apps[0].title;
    return 'NA';
  }

  var beginCheckoutPending = false;
  function trackBeginCheckout() {
    if (beginCheckoutPending) return; // GA4 FIX (F-16): dedupe -- several
    beginCheckoutPending = true;      // selectors below can share one click
    setTimeout(function () { beginCheckoutPending = false; }, 800);
    refreshCartThen(function () {
      var items = cartItemsFromConfig();
      if (!items.length) return;
      pushEvent('begin_checkout_ce', {
        currency: CFG.currency || 'INR',
        value: itemsValue(items),
        coupon: currentCoupon(),
        items: items
      });
    });
  }

  function bindCheckoutClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="checkout"], [href*="/checkout"], button[type="submit"][name="checkout"], .shopify-payment-button__button');
      if (!btn) return;
      trackBeginCheckout();
    }, true);
  }

  function findCardItemByVariant(variantId) {
    // GA4 FIX (F-08): cart rows also carry data-ga4-item, and they report
    // item_list_id "NA". Prefer a product card, and never let a cart row
    // supply list attribution.
    var sel = '[data-ga4-item*="\\"variant_id\\":' + variantId + '"], [data-ga4-item*="\\"variant_id\\": ' + variantId + '"], [data-ga4-item-id="' + variantId + '"]';
    var all = Array.prototype.slice.call(document.querySelectorAll(sel));
    var el = all.filter(function (n) { return !n.hasAttribute('data-ga4-cart-line'); })[0] || all[0];
    if (el) return enrichItem(parseItem(el), el);
    if (CFG.product && String(CFG.product.variant_id) === String(variantId)) {
      var prod = Object.assign({}, CFG.product);
      var pathParts = window.location.pathname.split('/products/');
      var handle = pathParts[1] ? pathParts[1].split('/')[0].split('?')[0] : '';
      var stored = readListAttr(prod.variant_id, prod.item_id, prod.product_id, variantId, handle);
      if (stored.item_list_id) {
        prod.item_list_id = stored.item_list_id;
        prod.item_list_name = stored.item_list_name;
        if (stored.index != null) prod.index = stored.index;
      }
      return prod;
    }
    return null;
  }

  function itemFromCartLine(line) {
    if (!line) return null;
    var variant = line.variant_id || line.id;
    var fromDom = findCardItemByVariant(variant);
    var stored = readListAttr(variant, fromDom && fromDom.item_id, line.sku, line.product_id, line.id, line.handle);
    var meta = readItemMeta(variant, fromDom && fromDom.item_id, line.sku, line.id, line.product_id);
    var price = line.final_price != null ? line.final_price / 100 : line.price != null ? line.price / 100 : 0;
    var compare = line.original_price != null ? line.original_price / 100 : price;
    var discount = Math.max(0, money(compare - price));
    return {
      item_id: (fromDom && fromDom.item_id) || line.sku || String(line.variant_id || line.id),
      item_name: real(fromDom && fromDom.item_name) || real(meta.item_name) || line.product_title || line.title || 'NA',
      affiliation: CFG.shop_name || 'NA',
      coupon: 'NA',
      discount: discount,
      item_brand: real(fromDom && fromDom.item_brand) || real(meta.item_brand) || line.vendor || CFG.shop_name || 'NA',
      item_category: real(fromDom && fromDom.item_category) || real(meta.item_category) || 'NA',
      item_category2: real(fromDom && fromDom.item_category2) || real(meta.item_category2) || 'NA',
      item_category3: real(fromDom && fromDom.item_category3) || real(meta.item_category3) || 'NA',
      item_category4: real(fromDom && fromDom.item_category4) || real(meta.item_category4) || 'NA',
      item_category5: real(fromDom && fromDom.item_category5) || real(meta.item_category5) || 'NA',
      // GA4 FIX (F-17): line.properties._ga4_list_id is the value stamped by
      // stampListProps() at add-to-cart time -- the most durable source,
      // since it survives page reloads where the original product card is
      // gone from the DOM.
      item_list_id: real(line.properties && line.properties._ga4_list_id) || real(fromDom && fromDom.item_list_id) || real(stored.item_list_id) || 'NA',
      item_list_name: real(line.properties && line.properties._ga4_list_name) || real(fromDom && fromDom.item_list_name) || real(stored.item_list_name) || 'NA',
      // GA4 FIX (F-12 / Audit-25-08): Normalize "Default Title" + blank to "NA".
      // `real()` only rejects null/''/NA but not "Default Title", so we do an
      // explicit check here to match snippets/ga4-item.liquid behaviour.
      item_variant: (function() {
        var fromCard = fromDom && fromDom.item_variant;
        var cached = meta.item_variant;
        var v = (fromCard && fromCard !== 'Default Title' && fromCard !== 'NA') ? fromCard
              : (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title
              : (cached && cached !== 'Default Title' && cached !== 'NA') ? cached
              : '';
        return v || 'NA';
      })(),
      price: money(price),
      quantity: line.quantity || 1,
      index: (fromDom && fromDom.index) || stored.index || 0
    };
  }

  function trackAddToCart(items) {
    if (!items || !items.length) return;
    var clean = items.map(function (it) {
      saveListAttr(it);
      return stripInternal(it, false);
    });
    pushEvent('add_to_cart_ce', {
      currency: CFG.currency || 'INR',
      value: itemsValue(clean),
      items: clean
    });
  }

  function trackRemoveFromCart(items) {
    if (!items || !items.length) return;
    var clean = items.map(function (it) { return stripInternal(it, false); });
    pushEvent('remove_from_cart_ce', {
      currency: CFG.currency || 'INR',
      value: itemsValue(clean),
      items: clean
    });
  }

  // GA4 FIX (F-18): pairing the i-th row with CFG.cart.items[i] broke as soon
  // as a free-gift or deal row shifted the indices. Match on variant id.
  function enhanceCartLineMarkup() {
    if (!CFG.cart || !CFG.cart.items) return;
    var byVariant = {};
    CFG.cart.items.forEach(function (it) {
      if (it && it.variant_id != null) byVariant[String(it.variant_id)] = it;
    });
    document.querySelectorAll('.cart-item, [id^="cartItem-"], [data-ga4-cart-line]').forEach(function (row) {
      if (row.getAttribute('data-ga4-item')) return;
      var vid = row.getAttribute('data-variant-id');
      var item = vid ? byVariant[String(vid)] : null;
      if (!item) return;
      row.setAttribute('data-ga4-item', JSON.stringify(item));
    });
  }

  // GA4 FIX (F-06/F-07): rebuild CFG.cart.items from the live cart so drawer
  // events reflect what is actually on screen, with free gifts excluded.
  function refreshCartThen(done) {
    var url = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        CFG.cart = CFG.cart || {};
        CFG.cart.item_count = cart.item_count;
        CFG.cart.total_price = (cart.total_price || 0) / 100;
        CFG.cart._raw = cart;
        CFG.cart.items = (cart.items || [])
          .filter(function (line) { return !isFreeLine(line); })
          .map(itemFromCartLine)
          .filter(Boolean);
        if (typeof done === 'function') done(cart);
      })
      .catch(function () { if (typeof done === 'function') done(null); });
  }

  function interceptCartFetch() {
    if (window.__perforaGA4FetchPatched) return;
    window.__perforaGA4FetchPatched = true;
    var nativeFetch = window.fetch;
    var previousCartItems = null;

    function snapshotCart(done) {
      refreshCartThen(function (cart) {
        if (!cart) return;
        previousCartItems = (cart.items || []).map(function (line) {
          return { key: line.key, variant_id: line.variant_id, quantity: line.quantity, line: line };
        });
        if (typeof done === 'function') done(cart);
      });
    }

    snapshotCart();

    // GA4 FIX (F-17): stamp the clicked card's list attribution onto the
    // outgoing /cart/add request as hidden ("_"-prefixed, so Shopify never
    // shows them in cart/checkout UI) line item properties. This is what lets
    // item_list_id / item_list_name survive all the way to the order-status
    // page purchase event in snippets/ga4-checkout.liquid, since that page has
    // no access to the sessionStorage the rest of this module relies on.
    function findVariantIdInFormData(fd) {
      return fd.get('id') || fd.get('id[]') || null;
    }

    function stampListProps(args) {
      try {
        var opts = args[1];
        if (!opts || !opts.body) return;
        var body = opts.body;

        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          if (body.has('properties[_ga4_list_id]')) return; // already stamped
          var variantId = findVariantIdInFormData(body);
          if (!variantId) return;
          var item = findCardItemByVariant(variantId);
          if (!item) return;
          body.append('properties[_ga4_list_id]', real(item.item_list_id) || 'NA');
          body.append('properties[_ga4_list_name]', real(item.item_list_name) || 'NA');
          return;
        }

        if (typeof body === 'string') {
          var parsed;
          try { parsed = JSON.parse(body); } catch (e) { return; }

          // Handle { items: [...] } array format (e.g. cart-deal-rail, multi-line add)
          if (parsed && Array.isArray(parsed.items)) {
            var changed = false;
            parsed.items = parsed.items.map(function (lineItem) {
              if (lineItem.properties && lineItem.properties._ga4_list_id) return lineItem;
              var vid2 = lineItem.id;
              var it2 = vid2 && findCardItemByVariant(vid2);
              if (!it2) return lineItem;
              lineItem.properties = Object.assign({}, lineItem.properties, {
                _ga4_list_id: real(it2.item_list_id) || 'NA',
                _ga4_list_name: real(it2.item_list_name) || 'NA'
              });
              changed = true;
              return lineItem;
            });
            if (changed) args[1] = Object.assign({}, opts, { body: JSON.stringify(parsed) });
            return;
          }

          // Handle single { id: ... } format
          if (!parsed || parsed.properties && parsed.properties._ga4_list_id) return;
          var vid = parsed.id;
          var it = vid && findCardItemByVariant(vid);
          if (!it) return;
          parsed.properties = Object.assign({}, parsed.properties, {
            _ga4_list_id: real(it.item_list_id) || 'NA',
            _ga4_list_name: real(it.item_list_name) || 'NA'
          });
          args[1] = Object.assign({}, opts, { body: JSON.stringify(parsed) });
        }
      } catch (e) {}
    }

    window.fetch = function () {
      var args = arguments;
      var input = args[0];
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var method = (args[1] && args[1].method) || (input && input.method) || 'GET';
      method = String(method).toUpperCase();
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && method !== 'GET';
      var isChange = /\/cart\/change(\.js)?(\?|$)/.test(url) && method !== 'GET';
      var isUpdate = /\/cart\/update(\.js)?(\?|$)/.test(url) && method !== 'GET';
      var before = previousCartItems ? previousCartItems.slice() : null;

      if (isAdd) stampListProps(args);

      return nativeFetch.apply(this, args).then(function (response) {
        var cloned = response.clone();
        if (isAdd || isChange || isUpdate) {
          cloned.json().then(function (data) {
            if (isAdd) {
              var lines = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
              // GA4 FIX (F-07): never fire add_to_cart for auto-added free gifts.
              lines = lines.filter(function (l) { return !isFreeLine(l); });
              var mapped = lines.map(itemFromCartLine).filter(Boolean);
              if (mapped.length) trackAddToCart(mapped);
              snapshotCart();
              return;
            }
            snapshotCart(function (cart) {
              if (!before) return;
              var afterMap = {};
              var beforeMap = {};
              (cart.items || []).forEach(function (line) { afterMap[line.key] = line; });
              before.forEach(function (prev) { beforeMap[prev.key] = prev; });

              var removed = [];
              var added = [];

              // Detect removals and quantity decreases
              before.forEach(function (prev) {
                if (isFreeLine(prev.line)) return; // GA4 FIX (F-07)
                var next = afterMap[prev.key];
                if (!next) {
                  var item = itemFromCartLine(prev.line);
                  if (item) { item.quantity = prev.quantity; removed.push(item); }
                } else if (next.quantity < prev.quantity) {
                  var item2 = itemFromCartLine(next);
                  if (item2) { item2.quantity = prev.quantity - next.quantity; removed.push(item2); }
                }
              });

              // Detect quantity increases (fires add_to_cart for the delta)
              (cart.items || []).forEach(function (next) {
                if (isFreeLine(next)) return;
                var prev = beforeMap[next.key];
                if (!prev) {
                  // Brand new line added via change/update (unusual but possible)
                  var itemNew = itemFromCartLine(next);
                  if (itemNew) added.push(itemNew);
                } else if (next.quantity > prev.quantity) {
                  var itemInc = itemFromCartLine(next);
                  if (itemInc) { itemInc.quantity = next.quantity - prev.quantity; added.push(itemInc); }
                }
              });

              if (removed.length) trackRemoveFromCart(removed);
              if (added.length) trackAddToCart(added);
            });
          }).catch(function () {});
        }
        return response;
      });
    };
  }

  // GA4 FIX (F-09): the observer fires at threshold 0.5, but the sibling filter
  // used to accept any pixel of overlap -- so a card peeking into a carousel
  // was reported as viewed. Same 50% test on both sides now.
  function isMostlyVisible(el) {
    var rect = el.getBoundingClientRect();
    var area = rect.width * rect.height;
    if (!area) return false;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var w = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
    var h = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
    return (w * h) / area >= 0.5;
  }

  function observeViewport() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-ga4-list]').forEach(function (list) {
        trackViewItemList(list, Array.prototype.slice.call(list.querySelectorAll('[data-ga4-item]')));
      });
      document.querySelectorAll('[data-ga4-promo]').forEach(trackViewPromotion);
      return;
    }

    var listObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el.hasAttribute('data-ga4-promo')) {
          trackViewPromotion(el);
          listObserver.unobserve(el);
          return;
        }
        if (el.hasAttribute('data-ga4-item')) {
          var list = el.closest('[data-ga4-list]');
          if (!list) return;
          var visibles = Array.prototype.slice
            .call(list.querySelectorAll('[data-ga4-item]'))
            .filter(isMostlyVisible);
          trackViewItemList(list, visibles);
        }
      });
    }, { threshold: 0.5 });

    function observeAll(root) {
      (root || document).querySelectorAll('[data-ga4-promo]').forEach(function (el) {
        listObserver.observe(el);
      });
      (root || document).querySelectorAll('[data-ga4-list] [data-ga4-item]').forEach(function (el) {
        listObserver.observe(el);
      });
      bindPromoClicks(root);
      bindItemClicks(root);
    }

    observeAllRef = observeAll;
    observeAll(document);

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          observeAll(node);
        });
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // ── SearchTap Recommendation Products tracking ────────────────────────────
  // SearchTap renders its autocomplete / recommendation panel outside Shopify
  // Liquid, so it has no data-ga4-* attributes. We observe the DOM for the
  // .st-recommended-cards container and stamp GA4 attributes dynamically.
  function bindSearchTapRecommendations() {
    var LIST_ID   = 'Recommendation Products';
    var LIST_NAME = 'Recommendation Products';
    var stampedCards = new WeakSet();

    function stampCard(card) {
      if (stampedCards.has(card)) return;
      stampedCards.add(card);
      var variantId  = card.getAttribute('data-st-product-activevariant-id') || '';
      var productId  = card.getAttribute('data-st-product-id')               || '';
      var productName = card.getAttribute('data-st-product-name')            || '';
      var handle     = card.getAttribute('data-st-product-handle')           || '';
      // Build a minimal GA4 item payload
      var itemPayload = {
        item_id:        variantId || productId,
        item_name:      productName,
        item_list_id:   LIST_ID,
        item_list_name: LIST_NAME,
        variant_id:     variantId,
        product_id:     productId,
        handle:         handle,
        index:          0,
        quantity:       1,
        price:          0
      };
      card.setAttribute('data-ga4-item', JSON.stringify(itemPayload));
      card.setAttribute('data-ga4-item-id', variantId || productId);
      // Save list attribution now so view_item / add_to_cart on the PDP carry it
      saveListAttr(itemPayload);
      // Fire select_item on click
      card.addEventListener('click', function () {
        saveListAttr(itemPayload);
        // Also save by handle so the PDP URL can match it
        if (handle) {
          try {
            var map = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
            var entry = { item_list_id: LIST_ID, item_list_name: LIST_NAME, index: 0 };
            map[handle] = entry;
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
          } catch (e) {}
        }
        pushEvent('select_item_ce', {
          currency: CFG.currency || 'INR',
          item_list_id:   LIST_ID,
          item_list_name: LIST_NAME,
          items: [{
            item_id:        itemPayload.item_id,
            item_name:      itemPayload.item_name,
            item_list_id:   LIST_ID,
            item_list_name: LIST_NAME,
            index:          0,
            quantity:       1
          }]
        });
      });
    }

    function stampContainer(container) {
      // Stamp list context on the wrapper itself
      container.setAttribute('data-ga4-list', '');
      container.setAttribute('data-ga4-list-id',   LIST_ID);
      container.setAttribute('data-ga4-list-name', LIST_NAME);
      container.querySelectorAll('.st-product-card').forEach(function (card, i) {
        var itemPayload = null;
        try {
          var raw = card.getAttribute('data-ga4-item');
          if (raw) { itemPayload = JSON.parse(raw); itemPayload.index = i; card.setAttribute('data-ga4-item', JSON.stringify(itemPayload)); }
        } catch (e) {}
        stampCard(card);
        // Update index after stamp
        try {
          var raw2 = card.getAttribute('data-ga4-item');
          if (raw2) { var p = JSON.parse(raw2); p.index = i; card.setAttribute('data-ga4-item', JSON.stringify(p)); }
        } catch (e) {}
      });
    }

    var stMo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          // The recommended products wrapper
          if (node.id === 'st-product-wrapper' || node.classList.contains('st-recommended-cards')) {
            stampContainer(node);
          }
          var recs = node.querySelectorAll && node.querySelectorAll('#st-product-wrapper, .st-recommended-cards');
          if (recs) recs.forEach(stampContainer);
          // Individual cards added after container was already stamped
          if (node.classList.contains('st-product-card')) stampCard(node);
        });
      });
    });
    stMo.observe(document.documentElement, { childList: true, subtree: true });

    // Catch any already-rendered panels (e.g. second open without page reload)
    document.querySelectorAll('#st-product-wrapper.st-recommended-cards, .st-recommended-cards').forEach(stampContainer);
  }

  window.PerforaGA4 = Object.assign(CFG, {
    pushEvent: pushEvent,
    trackAddToCart: trackAddToCart,
    trackRemoveFromCart: trackRemoveFromCart,
    trackBeginCheckout: trackBeginCheckout,
    saveListAttr: saveListAttr,
    // GA4 FIX (Audit-25-08): exposed so shop-by-concern.js (and any other
    // dynamic section) can ask the IntersectionObserver to re-scan newly
    // visible items after a tab/carousel switch without resetting state.
    reobserve: function (root) { if (observeAllRef) observeAllRef(root); },
    debug: false
  });

  ready(function () {
    seedItemMeta();
    interceptCartFetch();
    enhanceCartLineMarkup();
    observeViewport();
    trackViewItem();
    if (CFG.page_type === 'cart_page') trackViewCart();
    bindCartDrawerOpen();
    bindCheckoutClicks();
    bindSearchTapRecommendations();
  });
})();